"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { EnvelopeIcon, ShieldCheckIcon } from "@heroicons/react/24/outline";
import Image from "next/image";
import Link from "next/link";
import { Button } from "@/components/ui/button/Button";
import { Card } from "@/components/ui/card/Card";
import { FormField } from "@/components/ui/form-field/FormField";
import { Input } from "@/components/ui/input/Input";
import {
  ForgotPasswordSchema,
  type ForgotPasswordInput,
} from "@/lib/definitions";
import { useRequestPasswordResetMutation } from "@/hooks/api/usePasswordReset";

export default function ForgotPasswordForm() {
  const router = useRouter();
  const [submitted, setSubmitted] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState("");
  const [apiError, setApiError] = useState<string | null>(null);

  const requestResetMutation = useRequestPasswordResetMutation();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordInput>({
    resolver: zodResolver(ForgotPasswordSchema),
    defaultValues: { email: "" },
  });

  async function onSubmit(values: ForgotPasswordInput) {
    setApiError(null);
    try {
      await requestResetMutation.mutateAsync(values.email);
      setSubmittedEmail(values.email);
      setSubmitted(true);
    } catch (error) {
      setApiError(
        error instanceof Error
          ? error.message
          : "Failed to send reset email. Please try again.",
      );
    }
  }

  if (submitted) {
    return (
      <div className="flex min-h-screen flex-col bg-section">
        <div className="flex flex-1 flex-col items-center justify-center px-4 py-10">
          <Card className="w-full max-w-120">
            <Link href="/" className="mb-8 flex justify-center">
              <Image
                src="/images/logo/logo.webp"
                width={120}
                height={40}
                alt="Repayas"
                priority
                className="h-auto"
              />
            </Link>

            <div className="flex flex-col items-center text-center space-y-4 py-4">
              <div className="w-14 h-14 bg-primary-50 dark:bg-primary/30 rounded-full flex items-center justify-center">
                <EnvelopeIcon className="w-7 h-7 text-primary " />
              </div>
              <h1 className="text-2xl font-semibold text-text-primary dark:text-white/90">
                Check your inbox
              </h1>
              <p className="text-sm text-text-secondary dark:text-gray-400 max-w-xs">
                We sent a password reset link to{" "}
                <span className="font-semibold text-text-primary dark:text-white/90">
                  {submittedEmail}
                </span>
                . Check your email and click the link to reset your password.
              </p>
              <p className="text-xs text-gray-400 dark:text-text-secondary pt-2">
                Did not receive it? Check your spam folder.
              </p>
              <div className="pt-2 w-full">
                <Button
                  type="button"
                  onClick={() => {
                    setSubmitted(false);
                    setApiError(null);
                  }}
                  variant="secondary"
                  fullWidth
                  size="md"
                  rounded="lg"
                  className="h-12! text-base!"
                >
                  Try a different email
                </Button>
              </div>
            </div>
          </Card>

          <p className="mt-6 flex max-w-md items-center justify-center gap-2 px-4 text-center text-xs text-text-secondary dark:text-gray-400">
            <ShieldCheckIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
            <span>
              Your data is never shared or sold. Open Banking secured by
              TrueLayer.
            </span>
          </p>
        </div>

        <footer className="flex justify-center gap-6 px-4 pb-8 text-xs text-gray-400 dark:text-text-secondary">
          <Link
            href="/terms"
            className="hover:text-text-secondary dark:hover:text-gray-300"
          >
            Terms
          </Link>
          <Link
            href="/privacy"
            className="hover:text-text-secondary dark:hover:text-gray-300"
          >
            Privacy
          </Link>
          <Link
            href="/cookies"
            className="hover:text-text-secondary dark:hover:text-gray-300"
          >
            Cookies
          </Link>
        </footer>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-[#F4F6F8]">
      <div className="flex flex-1 flex-col items-center justify-center px-4 py-10">
        <Card className="w-full max-w-120">
          <Link href="/" className="mb-8 flex justify-center">
            <Image
              src="/images/logo/logo.webp"
              width={120}
              height={40}
              alt="Repayas"
              priority
              className="h-auto"
            />
          </Link>

          <div className="mb-8 text-center">
            <h1 className="mb-3 text-2xl font-semibold text-text-primary dark:text-white/90 sm:text-3xl">
              Forgot your password?
            </h1>
            <p className="text-sm text-text-secondary dark:text-gray-400">
              Enter your business email and we&apos;ll send you a reset link.
            </p>
          </div>

          {apiError ? (
            <div className="mb-5 rounded-lg border border-error-200 bg-error-50 px-4 py-3 dark:border-error-500/20 dark:bg-error-500/10">
              <p className="text-sm font-medium text-error-600 dark:text-error-400">
                {apiError}
              </p>
            </div>
          ) : null}

          <form
            onSubmit={handleSubmit(onSubmit)}
            className="space-y-5"
            noValidate
          >
            <FormField
              label="Email"
              htmlFor="resetEmail"
              error={errors.email?.message}
            >
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <Input
                    id="resetEmail"
                    type="email"
                    {...field}
                    value={field.value ?? ""}
                    placeholder="youremail@domain.co.uk"
                    autoComplete="email"
                    disabled={requestResetMutation.isPending}
                    error={!!errors.email}
                  />
                )}
              />
            </FormField>

            <div className="pt-1">
              <Button
                type="submit"
                loading={requestResetMutation.isPending}
                disabled={requestResetMutation.isPending}
                fullWidth
                variant="primary"
                size="md"
                rounded="lg"
                className="h-12! text-base! text-white"
              >
                Send reset link
              </Button>
            </div>
          </form>

          <p className="mt-6 text-center text-sm text-text-secondary dark:text-gray-400">
            Remembered it?{" "}
            <Link
              href="/signin"
              className="font-semibold text-text-primary hover:underline dark:text-white/90"
            >
              Sign in
            </Link>
          </p>
        </Card>

        <p className="mt-6 flex max-w-md items-center justify-center gap-2 px-4 text-center text-xs text-text-secondary dark:text-gray-400">
          <ShieldCheckIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
          <span>
            Your data is never shared or sold. Open Banking secured by
            TrueLayer.
          </span>
        </p>
      </div>

      <footer className="flex justify-center border-t border-gray-200 py-8 gap-6 px-4 pb-8 text-xs text-gray-400 dark:text-text-secondary">
        <Link
          href="/terms"
          className="hover:text-text-secondary dark:hover:text-gray-300"
        >
          Terms
        </Link>
        <Link
          href="/privacy"
          className="hover:text-text-secondary dark:hover:text-gray-300"
        >
          Privacy
        </Link>
        <Link
          href="/cookies"
          className="hover:text-text-secondary dark:hover:text-gray-300"
        >
          Cookies
        </Link>
      </footer>
    </div>
  );
}
